import { useMutation, useQuery } from '@tanstack/react-query';

import { useXFetch } from '~/shared/utils/useXFetch';
import { toaster } from '~/shared/utils/toaster';

export type RoleArnInfo = { roleArn: string };
type UpdateRoleArnPayload = RoleArnInfo & { skipValidation?: boolean };

type RoleArnEndpointConfig = {
  accountId?: string;
  isOrganization: boolean;
};

type RoleArnInteractionStateParams = {
  forceEditable: boolean;
  roleArn?: string;
  hasRootError: boolean;
  isFormLoading: boolean;
  isFormValid: boolean;
  isFormDirty: boolean;
  isRoleArnLoading: boolean;
  isUpdatePending: boolean;
  isDeletePending?: boolean;
};

type RoleArnErrorStateParams = {
  idPrefix: string;
  fieldErrorMessage?: string;
  hasRootError: boolean;
};

function getRoleArnUrls({ accountId, isOrganization }: RoleArnEndpointConfig) {
  const url = '/api/v1/accounts/role-arn';
  const accountScopedUrl = `/api/v1/accounts/${encodeURIComponent(accountId ?? '')}/role-arn`;

  return {
    fetchUrl: isOrganization ? accountScopedUrl : url,
    updateUrl: isOrganization ? accountScopedUrl : url,
    deleteUrl: accountScopedUrl,
  };
}

export function getRoleArnErrorState({
  idPrefix,
  fieldErrorMessage,
  hasRootError,
}: RoleArnErrorStateParams) {
  const fieldErrorId = `${idPrefix}-field-error`;
  const footerErrorId = `${idPrefix}-footer-error`;
  const describedBy = [
    fieldErrorMessage ? fieldErrorId : undefined,
    hasRootError && !fieldErrorMessage ? footerErrorId : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  return {
    hasError: Boolean(fieldErrorMessage || hasRootError),
    fieldErrorId,
    footerErrorId,
    describedBy: describedBy || undefined,
  };
}

export function getRoleArnInteractionState({
  forceEditable,
  roleArn,
  hasRootError,
  isFormLoading,
  isFormValid,
  isFormDirty,
  isRoleArnLoading,
  isUpdatePending,
  isDeletePending = false,
}: RoleArnInteractionStateParams) {
  const hasRoleArn = Boolean(roleArn);
  const isBusy = isFormLoading || isRoleArnLoading;
  const showEditButton = !forceEditable && hasRoleArn && !hasRootError && !isUpdatePending;
  const isApplyButtonDisabled =
    !isFormValid ||
    (!isFormDirty && hasRoleArn && !forceEditable && !hasRootError) ||
    isBusy ||
    isUpdatePending;

  return {
    showEditButton,
    showApplyButton: !showEditButton,
    isInputDisabled: (!forceEditable && hasRoleArn && !hasRootError) || isBusy,
    isApplyButtonDisabled,
    isEditButtonDisabled: isBusy,
    isDeleteButtonDisabled: !hasRoleArn || isBusy || isUpdatePending || isDeletePending,
  };
}

export function useRoleArnMutations({
  accountId,
  isOrganization = false,
  onUpdateSuccess,
  onUpdateError,
  onDeleteSuccess,
}: {
  accountId?: string;
  isOrganization?: boolean;
  onUpdateSuccess: () => void;
  onUpdateError: (error: Error) => void;
  onDeleteSuccess: () => void;
}) {
  const api = useXFetch();
  const { fetchUrl, updateUrl, deleteUrl } = getRoleArnUrls({ accountId, isOrganization });

  const {
    data: roleArnInfo,
    isLoading: isRoleArnLoading,
    refetch: refetchRoleArn,
  } = useQuery({
    queryKey: ['getRoleArn', accountId],
    queryFn: async () => {
      return await api<RoleArnInfo>(fetchUrl);
    },
    enabled: isOrganization ? Boolean(accountId) : true,
  });

  const updateRoleArnMutation = useMutation({
    mutationFn: async (values: UpdateRoleArnPayload) => {
      return await api<RoleArnInfo>(updateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          roleArn: values.roleArn,
          ...(values.skipValidation && { skipValidation: true }),
        }),
      });
    },
    onSuccess: () => {
      toaster({
        title: 'IAM Role Updated',
        message: 'The IAM role was updated successfully',
        variant: 'success',
      });
      onUpdateSuccess();
    },
    onError: (error: Error) => {
      const isInternalServerError = error.message === 'Internal server error';
      const toast = isInternalServerError
        ? { title: 'Error Inputting Role', message: 'Failed to input IAM role' }
        : isOrganization
          ? { title: 'Invalid IAM Role', message: error.message }
          : null;
      if (toast) {
        toaster({ ...toast, variant: 'error' });
      }
      onUpdateError(error);
    },
  });

  const deleteRoleArnMutation = useMutation({
    mutationFn: async () => {
      return await api(deleteUrl, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      toaster({
        title: 'IAM Role Removed',
        message: 'The IAM role was removed successfully',
        variant: 'success',
      });
      onDeleteSuccess();
    },
    onError: (error: Error) => {
      toaster({
        title: 'Error Removing Role',
        message: error.message || 'Failed to remove IAM role',
        variant: 'error',
      });
    },
  });

  return {
    roleArnInfo,
    roleArn: roleArnInfo?.roleArn,
    isRoleArnLoading,
    refetchRoleArn,
    updateRoleArnMutation,
    deleteRoleArnMutation,
  };
}
